import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Table } from 'antd';
import ScenarioCard from 'components/molecules/PriceSimulator/ScenarioCard';
import SelectionDropDown from 'components/molecules/SelectionDropDown';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import _ from 'lodash';
import tw from 'twin.macro';
import { ConfigKeys } from 'utils/Enums';
import { getConfig } from 'utils/helperMethods';

export const scenarioInfoTableId = 'scenario-card-table';

type ScenariosContainerType = {
    selectedRowKeys: Array<number>;
    scenarioCardsDetails: any;
    selectedCard: number;
    setSelectedCard: Function;
    onRemoveCardHandler: Function;
    comparisonTile: any;
    orderedSelectedInputKpiOpitions: any;
    setOrderedSelectedInputKpiOpitions: Function;
};

const ScenariosContainer = ({
    scenarioCardsDetails,
    selectedRowKeys,
    selectedCard,
    setSelectedCard,
    onRemoveCardHandler,
    comparisonTile,
    orderedSelectedInputKpiOpitions,
    setOrderedSelectedInputKpiOpitions,
}: ScenariosContainerType) => {
    const { appConfig } = useContext(UserContext);

    const { kpiMetrics } = getConfig(ConfigKeys.PRICE_SIMULATOR, appConfig);
    const [selectedInputKpiOptions, setSelectedInputKpiOptions] = useState<any[]>([]);

    const [inputKpiOptions, setInputKpiOptions] = useState<any[]>(initiliseInputKpisForMultiSelect);

    useEffect(() => {
        const originalOrder = kpiMetrics?.map((kpi: any, index: number) => {
            return {
                label: kpi.name,
                value: kpi.value,
            };
        });

        const newResult: any = [];

        originalOrder.forEach((item: any) => {
            if (selectedInputKpiOptions.includes(item.value)) {
                newResult.push(item);
            }
        });
        setOrderedSelectedInputKpiOpitions(newResult);
    }, [selectedInputKpiOptions]);

    useEffect(() => {
        // destination elements
        const tableScrollableElement =
            document.getElementById('scenario-cards')?.getElementsByClassName('ant-table-body') || [];
        // source element
        const kpiTable = document.getElementById(`scenario-kpi-table`)?.getElementsByClassName('ant-table-body')[0];

        // keep all the scrollable elements in a single array
        const targetElements = [kpiTable];
        Array.from(tableScrollableElement).forEach((table: any) => {
            targetElements.push(table);
        });

        targetElements.forEach((element: any, index) => {
            element.addEventListener('scroll', function () {
                // Update the scroll position of all other scrollable elements
                targetElements.forEach((el: any, i) => {
                    if (i !== index) {
                        el.scrollTop = element.scrollTop;
                    }
                });
            });
        });
    }, [scenarioCardsDetails]);

    function initiliseInputKpisForMultiSelect() {
        const inputkpiMappingOptions = kpiMetrics?.map((kpi: any, index: number) => {
            return {
                label: kpi.name,
                value: kpi.value,
            };
        });
        const selectedInputkpiMappingOptions = kpiMetrics?.map((kpi: any, index: number) => kpi.value);
        // set default SelectedValues
        setSelectedInputKpiOptions(selectedInputkpiMappingOptions);
        setOrderedSelectedInputKpiOpitions(inputkpiMappingOptions);
        return inputkpiMappingOptions;
    }

    const columns: any = [
        {
            title: ' ',
            dataIndex: 'kpiKey',
            width: 190,
        },
    ];

    return (
        <div className="flex" id="price-sim-comparison-scenarios-container">
            <div className="mr-4" style={{ width: '210px' }}>
                <SelectionDropDown
                    list={inputKpiOptions}
                    checkedListObject={''}
                    disabled={false} // make it optional
                    setSelectedList={setSelectedInputKpiOptions}
                    selectedList={selectedInputKpiOptions || []}
                    className="dropdown-width-multi-select"
                    defaultDisplayLabel="KPI"
                    style={{ height: '40px', width: '210px' }}
                    innerDivStyle={{
                        borderColor: colorConstants.greyAlt3,
                        borderBottom: 'none',
                        backgroundColor: colorConstants.grey,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                    }}
                />
                <CustomTablePriceScenario
                    bordered
                    columns={columns}
                    pagination={false}
                    scroll={{ x: '100%', y: 270 }}
                    className=""
                    sticky
                    loading={false}
                    dataSource={[
                        ...orderedSelectedInputKpiOpitions.map((item: any) => {
                            return { kpiKey: item.label };
                        }),
                    ]}
                    id={'scenario-kpi-table'}
                />
            </div>
            <div className=" flex gap-4 overflow-y-scroll  " id={'scenario-cards'}>
                {scenarioCardsDetails &&
                    selectedRowKeys.map((item: any, index: number) => {
                        return (
                            <ScenarioCard
                                scenarioId={item}
                                key={item}
                                tableId={`${scenarioInfoTableId}`}
                                cardDetails={scenarioCardsDetails[item]}
                                selected={item === selectedCard}
                                setSelectedCard={setSelectedCard}
                                onRemoveCardHandler={onRemoveCardHandler}
                                comparisonTile={comparisonTile}
                                orderedSelectedInputKpiOpitions={orderedSelectedInputKpiOpitions}
                            />
                        );
                    })}
                {/* adding empty placeholders  */}
                {[...Array(5 - selectedRowKeys.length)].map((item: any) => {
                    return (
                        <div
                            style={{ minWidth: '226px', maxWidth: '226px' }}
                            className="font-xxs flex items-center rounded justify-center border border-dashed border-borderGrey px-8 text-center text-promoColour"
                        >
                            Select scenarios from the “Select Plans” button above and add to compare
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ScenariosContainer;

export const CustomTablePriceScenario = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-thead > tr > th {
        color: ${colorConstants.darkGrey};
        background: white;
        padding: 0px;
        height: 27px;
        text-align: center;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        width: 50px;
    }
    .ant-table-tbody > tr > td,
    .ant-table-thead > tr > th,
    .ant-table tfoot > tr > td,
    .ant-table tfoot > tr > th {
        padding: 10px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    .ant-table-cell {
        text-align: center;
    }

    border: ${'1px solid #DBDFE9'};
`;
